import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableHead, TableRow,
  CircularProgress, Alert, Chip, Button, Select, MenuItem, FormControl, InputLabel
} from '@mui/material';
import { mavEnforcementService } from '../services/mavEnforcementService';

const PHASE_LABELS = { 1: '60th', 2: '70th', 3: '80th', 4: '90th', 5: '99th' };

function MAVEnforcementDashboard() {
  const [distributions, setDistributions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterCategory, setFilterCategory] = useState('');

  useEffect(() => {
    loadDistributions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterCategory]);

  const loadDistributions = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await mavEnforcementService.listDistributions({
        product_category: filterCategory || undefined,
      });
      setDistributions(data ?? []);
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAdvancePhase = async (id) => {
    try {
      await mavEnforcementService.advancePhase(id);
      loadDistributions();
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message);
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>MAV Enforcement (Statistical)</Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Percentile-based Maximum Applicable Value enforcement with phased progression (60th to 99th percentile)
      </Typography>

      <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Product Category</InputLabel>
          <Select value={filterCategory} label="Product Category" onChange={(e) => setFilterCategory(e.target.value)}>
            <MenuItem value="">All</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? <CircularProgress /> : (
        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Product Category</TableCell>
                <TableCell>Subcategory</TableCell>
                <TableCell>Region</TableCell>
                <TableCell>Phase</TableCell>
                <TableCell>Percentile</TableCell>
                <TableCell>Threshold (kgCO2e)</TableCell>
                <TableCell>Mean</TableCell>
                <TableCell>Std Dev</TableCell>
                <TableCell>Sample Size</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {distributions.map((d) => (
                <TableRow key={d.mav_distribution_id}>
                  <TableCell>{d.product_category}</TableCell>
                  <TableCell>{d.product_subcategory || '-'}</TableCell>
                  <TableCell>{d.region || 'Global'}</TableCell>
                  <TableCell><Chip label={`Phase ${d.current_phase}`} size="small" color={d.current_phase >= 4 ? 'success' : 'warning'} /></TableCell>
                  <TableCell>{PHASE_LABELS[d.current_phase]}</TableCell>
                  <TableCell>{d.percentiles?.[`p${PHASE_LABELS[d.current_phase]?.replace('th', '')}`]?.toFixed(2) || '-'}</TableCell>
                  <TableCell>{parseFloat(d.mean_value)?.toFixed(2) || '-'}</TableCell>
                  <TableCell>{parseFloat(d.std_deviation)?.toFixed(2) || '-'}</TableCell>
                  <TableCell>{d.sample_size}</TableCell>
                  <TableCell>
                    {d.current_phase < 5 && (
                      <Button size="small" onClick={() => handleAdvancePhase(d.mav_distribution_id)}>
                        Advance Phase
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {distributions.length === 0 && (
                <TableRow><TableCell colSpan={10} align="center">No distributions found</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
}

export default MAVEnforcementDashboard;
